import { useEventBus } from '@vueuse/core';
import {
    BoxGeometry,
    FrontSide,
    LinearMipmapLinearFilter,
    Mesh,
    MeshStandardMaterial,
} from 'three';
import { onMounted, watch } from 'vue';

import { requestIdleCallback } from '@resn/gozer-misc';
import { useRenderer } from '@resn/gozer-vue';

import CardRawMaterial2 from '@/components/gl/materials/CardRawMaterial2';
import { RAW_CARD_ASPECT, RAW_CARD_THICKNESS } from '~/core/constants';
import { loadBitmapTexture } from '~/libs/three/loadBitmapTexture';
import { useGlobalAssets } from '~/providers/GlobalAssets';
import { applyCardTextureRotation } from '~/utils/card-texture-utils';
import { replaceMaterialSnippets } from '~/utils/replace-material-snippets';

export { RAW_CARD_ASPECT, RAW_CARD_THICKNESS };

/**
 * Shared CardRawMaterial stack + env/matcap wiring + box mesh for CardRaw / CardWaxChrome.
 */
export const useRawCard = ({
    props,
    name = 'CardRaw',
    preRevealMatcapId = 'matcapIrridescent',
} = {}) => {
    if (!props?.data) {
        throw new Error('useRawCard: expected { props } with props.data');
    }

    const bus = useEventBus('card-inner');
    const { renderer } = useRenderer();

    const createMaterials = () => {
        const matEdges = new MeshStandardMaterial({
            name: 'CardEdgesMaterial',
            transparent: false,
            side: FrontSide,
            color: '#ddd',
            roughness: 0.2,
            metalness: 0.8,
            defines: {
                USE_UV: true,
            },
        });

        replaceMaterialSnippets({
            material: matEdges,
            uniforms: {
                progress: { value: 0 },
            },
            fragment: [
                {
                    snippet: '#include <common>',
                    newSnippet: /* glsl */ `uniform float progress;`,
                },
                {
                    snippet: '#include <dithering_fragment>',
                    newSnippet: /* glsl */ `
                    gl_FragColor.rgb = mix(vec3(0.0), gl_FragColor.rgb, progress);
                `,
                },
            ],
        });

        const matFront = new CardRawMaterial2({ side: FrontSide });
        const matBack = new CardRawMaterial2({ side: FrontSide });
        const materials = [matEdges, matEdges, matEdges, matEdges, matFront, matBack];

        return [materials, matEdges, matFront, matBack];
    };

    const [materials, matEdges, matFront, matBack] = createMaterials();

    const shaders = [matFront, matBack];
    const shadersAll = [matEdges, ...shaders];

    const geometry = new BoxGeometry(RAW_CARD_ASPECT, 1, RAW_CARD_THICKNESS);

    const mesh = new Mesh(geometry, materials);
    mesh.name = `${name}Mesh`;

    useGlobalAssets(({ data }) => {
        const envMap = data.envMapPMREM || data.envMap;
        shadersAll.forEach((shader) => {
            shader.envMap = envMap;
            shader.needsUpdate = true;
        });
        shaders.forEach((shader) => {
            shader.uniforms.tMatcapGloss.value = data.matcapDiffuse;
            shader.uniforms.tMatcapIrri.value = data[preRevealMatcapId];
            shader.uniforms.tMatcapIrri.value = data[preRevealMatcapId];
            shader.uniforms.uNoise.value = data.pNoise;
            shader.uniforms.uChromeNoise.value = data.pNoiseNormal;
        });
    });

    const setTexture = ({ url, direction, side = 'front' }) => {
        if (!url) {
            console.warn(`No ${side} image URL provided for texture`);
            return;
        }

        loadBitmapTexture(url, { minFilter: LinearMipmapLinearFilter }).then((texture) => {
            const mat = side === 'front' ? matFront : matBack;
            applyCardTextureRotation(
                `Card ${side.charAt(0).toUpperCase() + side.slice(1)}`,
                direction,
                texture
            );

            mat.map?.dispose();
            mat.map = texture;
            mat.needsUpdate = true;

            bus.emit(`texture-loaded-${side}`);

            requestIdleCallback(() => {
                renderer.initTexture(texture);
            });
        });
    };

    onMounted(() => {
        const { imageFront, imageBack, direction } = props.data;

        if (imageFront) setTexture({ url: imageFront, direction, side: 'front' });
        if (imageBack) setTexture({ url: imageBack, direction, side: 'back' });
    });

    watch(
        () => props.data.imageFront,
        (url) => setTexture({ url, direction: props.data.direction, side: 'front' })
    );
    watch(
        () => props.data.imageBack,
        (url) => setTexture({ url, direction: props.data.direction, side: 'back' })
    );

    return {
        mesh,
        matFront,
        matBack,
        matEdges,
        shaders,
        shadersAll,
        setTexture,
    };
};
